import qs from 'qs';
import axios, { AxiosResponse } from 'axios';
import {
    IOnlineApiService,
    IRequestChunkDataParams,
    IRequestResourceParams,
    IRequestCreateDocParams,
    IRequestDocInfoParams,
    IRequestExportSlideParams,
    IRequestExportSnapshotParams,
    IRequestTitleParams,
    IRequestUserInfoParams,
} from '../core/interfaces/online-api-service.interface';
import { ILogger } from '../../../core/slide-data-services.core';
import {
    Reporter007,
    Reporter007ActionType,
} from '@src/slide/infrastructure/module/dependent/report/real-time-report/constant';
import { Base64String } from '../../../utils/base64-utils';
import { getBkn, getXSRF } from '../../../utils/cookie-utils';

// 自定义参数序列化函数
const paramsSerializer = params => qs.stringify(params, { encode: true });

export class OnlineApiService implements IOnlineApiService {
    private onlineDocPrefix = '/cgi-bin/online_docs';
    private cgiGoPrefix = '/cgi-go';
    private dopApiPrefix = '/dop-api';
    private v1ExportPrefix = '/v1/export';

    public constructor(private logger: ILogger) {}

    /**
     * 分片请求文档数据
     * @param params
     * @param params.notAlreadyCreate
     * 1: 新导入文档后端尚未解析结束，渐进式分片返回
     * 0(默认): 已存在在线文档后端全量数据，分片返回数据
     * @returns
     */
    public getChunkData = (params: IRequestChunkDataParams) => axios.get('/dop-api/slide/pages/get', { params: { ...params, xsrf: getXSRF() } });

    public pollingResourceData = (globalPadId: string, params: IRequestResourceParams) => axios.get(`/api/slide/${globalPadId}/resources`, {
        params,
        paramsSerializer,
    });

    public pollingDocStatus = (globalPadId: string) => axios.get(`/api/weboffice/files/${globalPadId}/status`);

    public getDocInfo = (params: IRequestDocInfoParams) => axios.get(`${this.onlineDocPrefix}/doc_info`, { params: { ...params, xsrf: getXSRF() } });

    public updateTitle = (params: IRequestTitleParams) => {
        const { padId, domainId, title, timestamp = Date.now() } = params;
        const requestParams = `pad_id=${padId}&type=1&ver=2&domain_id=${domainId}&xsrf=${getXSRF()}&version=2&title=${encodeURIComponent(title)}&timestamp=${timestamp}`;
        return axios.post(`${this.onlineDocPrefix}/doc_changetitle?${requestParams}`);
    };

    public getUserInfosByPadID = (params: IRequestUserInfoParams) => axios.get('/sdc/getcollabroomuserinfos', { params: { ...params, xsrf: getXSRF() } });

    /**
     * 生成文档副本
     * @param domainId 文档domainid
     * @param localPadId 文档id
     */
    public generateDuplicateDoc = async (domainId: string, padId: string, title: string) => {
        const form = new FormData();
        form.append('domain_id', domainId);
        form.append('title', title);
        form.append('pad_id', padId);
        form.append('xsrf', getXSRF());
        const res = await axios.post(`${this.onlineDocPrefix}/copydoc_async`, form);
        if (res.data.cgicode !== 0 || !res.data.doc_id) {
            return res;
        }
        const { domain_id, pad_id } = res.data.doc_id;
        form.set('domain_id', domain_id);
        form.set('pad_id', pad_id);
        form.delete('title');
        return await this.duplicateLoop(form);
    };

    public duplicateLoop = async (form: FormData, interval = 1000): Promise<AxiosResponse<any, any>> => {
        const res = await axios.post(`${this.onlineDocPrefix}/copydoc_loop`, form);
        /*
          如果retcode 是10027，说明文档还在复制中，一定间隔后继续轮询
          如果retcode 是其他错误，直接报错
          如果retcode 为0，说明复制成功，可以跳转。
        */
        if (res.data.retcode === 10027) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(this.duplicateLoop(form, interval + 1000));
                }, interval);
            });
        }
        return res;
    };

    public transDocVisible = (domainId: string, padId: string) => {
        const form = new FormData();
        form.append('domain_id', domainId);
        form.append('pad_id', padId);
        form.append('xsrf', getXSRF());
        return axios.post(`${this.onlineDocPrefix}/doc_humtrans`, form);
    };

    public createDocByData = async (params: IRequestCreateDocParams) => {
        try {
            const res = await axios.post(
                '/cgi-bin/translate/translate_save',
                {
                    ...params,
                    xsrf: getXSRF(),
                    commands_format: 1,
                    doc_type: 4,
                },
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                    },
                },
            );
            this.logger.monitor?.reportStatus?.(Reporter007ActionType.CREATE_DOC_BY_SNAPSHOT, Reporter007.SUCCESS, '', [
                params.globalPadId,
            ]);
            return res;
        } catch (err) {
            this.logger.monitor?.reportStatus?.(
                Reporter007ActionType.CREATE_DOC_BY_SNAPSHOT,
                Reporter007.ERROR,
                err.toString(),
                [params.globalPadId],
            );
            throw err;
        }
    };

    public asyncExportSnapshot = (data: IRequestExportSnapshotParams, timeout = 10000) => {
        const url = `${this.v1ExportPrefix}/export_office`;
        const form = new FormData();
        form.append('docId', data.docId);
        form.append('content', Base64String.fromUint8Array(data.content));
        if (data.version) {
            form.append('version', data.version);
        }

        return axios.post(url, form, { timeout });
    };

    /**
     * 根据模板生成新文档
     * @param docType 文档类型
     * @param templateId 模板id
     * @param title 文档标题
     * @param createType 类型
     */
    public createDocNewByTemplate = (docType: string, templateId: number, title: string, createType: number) => {
        const form = new FormData();
        form.append('create_type', `${createType}`);
        form.append('doc_type', docType);
        form.append('template_id', `${templateId}`);
        form.append('title', title);
        form.append('xsrf', getXSRF());
        form.append('folder_id', '');
        form.append('bkn', `${getBkn()}`);
        form.append('hum', '1'); // 新建的文档需在编辑页保存后才沉淀到列表页
        return axios.post(`${this.onlineDocPrefix}/createdoc_new`, form, { timeout: 30000 });
    };

    /**
     * 导出到电脑
     * @param globalPadId 文档id
     */
    public exportSlideToPC = (globalPadId: string) => {
        const url = `${this.cgiGoPrefix}/common-notify/view`;
        const form = new FormData();
        form.append('xsrf', getXSRF());
        form.append('padId', globalPadId);
        return axios.post(url, form);
    };

    /**
     * 获取举报token
     * @param domainId
     * @param padId
     */
    public getUserReportToken = (domainId: string, padId: string) => {
        const form = new FormData();
        form.append('domain_id', domainId);
        form.append('pad_id', padId);
        form.append('xsrf', getXSRF());
        form.append('dataType', '0');
        return axios.post(`${this.onlineDocPrefix}/support_gettoken`, form);
    };

    /**
     * 获取文档权限信息（*不适用于收集表）
     * @param padId 文档id
     * @param creatorDomain 文档domain
     */
    public getAuthInfo = (padId: string, creatorDomain: string) => axios.get(`${
        this.onlineDocPrefix
    }/auth_info/get?localPadId=${padId}&type=1&ver=2&domain=${creatorDomain}&xsrf=${getXSRF()}`);

    /**
     * 拉取历史版本
     * @param globalPadId 文档id（带domainId前缀）
     * @param rev
     * @param fRev
     */
    public getHistorySlide = (globalPadId: string, rev: number, fRev: number) => axios.get(`${
        this.dopApiPrefix
    }/get/slide?padId=${globalPadId}&rev=${rev}&fRev=${fRev}&timestamp=${new Date().getTime()}&xsrf=${getXSRF()}`);

    /**
     * 获取文档有效期信息
     * @param domainId 文档domainid
     * @param padId 文档id
     */
    public getAuthExpTime = (domainId: string, padId: string) => axios.get(`${this.onlineDocPrefix}/auth_exp/get?domainId=${domainId}&localPadId=${padId}&xsrf=${getXSRF()}`);

    /**
     * 恢复删除的文档
     * @param domainId 文档domainId
     * @param localPadId 文档padId
     */
    public recoverDoc(domainId: string, localPadId: string) {
        const form = new FormData();
        form.append('domain_id', domainId);
        form.append('pad_id', localPadId);
        form.append('xsrf', getXSRF());
        return axios.post(`${this.onlineDocPrefix}/doc_recover`, form);
    }

    /**
     * 查询导出状态
     * @param operationId asyncExportSlide返回的operationId
     * @param timeout Http请求超时时间
     */
    public queryExportProgress = (operationId: string, timeout = 10000) => axios.get(`${this.v1ExportPrefix}/query_progress?operationId=${operationId}`, { timeout });

    /**
     * 异步导出PPT文件
     * @param globalPadId globalPadId
     * @param exportArgs
     * @param timeout HTTP请求超时时间
     */
    public asyncExportSlide = (exportArgs: IRequestExportSlideParams, timeout = 10000) => {
        const url = `${this.v1ExportPrefix}/export_office`;
        const form = new FormData();
        form.append('docId', exportArgs.globalPadId);
        exportArgs.exportWithAttachment && form.append('exportType', '1');
        return axios.post(url, form, { timeout });
    };

    /**
     * 获得用户信息
     */
    public getUserInfo = (onlyCheck?: boolean) => {
        if (onlyCheck) {
            return axios.get(`${this.onlineDocPrefix}/user_info?xsrf=${getXSRF()}&get_vip_info=1&only_check=1`);
        }
        return axios.get(`${this.onlineDocPrefix}/user_info?xsrf=${getXSRF()}&get_vip_info=1`);
    };

    /**
     * 获取通知数
     */
    public getNotifyNumbers() {
        return axios.get(`${this.onlineDocPrefix}/notify_getnum?&xsrf=${getXSRF()}&bkn=${getBkn()}`);
    }

    /**
     * 清除通知数
     */
    public clearNotifyNumbers() {
        return axios.post(`${this.onlineDocPrefix}/user_flag/setRedDot?&xsrf=${getXSRF()}&bkn=${getBkn()}&type=8&flag=0`);
    }

    /**
     * 拉取修订记录
     * @param globalPadId 文档id（带domainId前缀）
     * @param page 第几页
     */
    public getHistory(globalPadId: string, page: number) {
        return axios.get(`${
            this.dopApiPrefix
        }/get/history?padId=${globalPadId}&page=${page}&timestamp=${new Date().getTime()}&xsrf=${getXSRF()}`);
    }

    /**
     * 文档回滚
     * @param globalPadId 文档id（带domainId前缀）
     * @param rev 版本号
     * @param version 第几版
     */
    public revert(globalPadId: string, rev: number, version: number, timeout = 0) {
        return axios.get(`${this.dopApiPrefix}/revert?padId=${globalPadId}&rev=${rev}&v=${version}&xsrf=${getXSRF()}`, {
            timeout,
        });
    }

    /**
     * @param globalPadId
     * @param page 指定哪一页
     * @param limit 每页记录数 default:20
     */
    public getVersion(globalPadId: string, page: number, limit: number) {
        return axios.get(`${
            this.cgiGoPrefix
        }/padinfo/get_version_record?globalPadId=${globalPadId}&page=${page}&limit=${limit}&xsrf=${getXSRF()}`);
    }

    /**
     * [编辑态下的远程演示(编辑态跟随)] 获取信息
     * @param docId 文档 id，即 globalPadId
     * @param presentId 演示房间 id
     * @returns null 表示接口错误，外部不需要关心具体 code
     */
    public async getPresentInfo(docId: string, presentId: string) {
        return axios.get('/room-api/presentinfo', {
            responseType: 'json',
            withCredentials: true,
            params: {
                docid: docId,
                presentid: presentId,
            },
        });
    }

    /**
     * [编辑态下的远程演示(编辑态跟随)] 设置主讲人
     * @param docId 文档 id，即 globalPadId
     * @param presentId 演示房间 id
     * @param uid 主讲人 uid
     */
    public async setPresentInfo(docId: string, presentId: string, uid: string) {
        return axios.post(
            '/room-api/presentinfo',
            {
                docid: docId,
                presentid: presentId,
                uid,
            },
            {
                headers: { 'Content-Type': 'application/json' },
                responseType: 'json',
                withCredentials: true,
            },
        );
    }
}
